import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CheckCircle, XCircle } from 'lucide-react'
import { InvoiceUploadForm } from '@/components/financials/InvoiceUploadForm'
import { useLocalization } from '@/contexts/LocalizationContext'

interface PayoutActionsProps {
  statementId: string
  /** `document` is required by the backend on both actions. */
  onApprove: (statementId: string, document: File, notes?: string) => Promise<void>
  onDispute: (statementId: string, reason: string, document: File) => Promise<void>
  disabled?: boolean
  /** Show Approve button (e.g. only for Pending Approval). Default true. */
  showApprove?: boolean
  /** Show Dispute button (e.g. only for approved/processing/paid). Default true. */
  showDispute?: boolean
}

export function PayoutActions({
  statementId,
  onApprove,
  onDispute,
  disabled = false,
  showApprove = true,
  showDispute = true,
}: PayoutActionsProps) {
  const { t } = useLocalization()
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showDisputeDialog, setShowDisputeDialog] = useState(false)

  return (
    <>
      <div className="flex items-center gap-2">
        {showApprove && (
          <Button
            size="sm"
            onClick={() => setShowApproveDialog(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={disabled}
          >
            <CheckCircle className="me-1 h-4 w-4" />
            {t('financials.approve')}
          </Button>
        )}
        {showDispute && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowDisputeDialog(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={disabled}
          >
            <XCircle className="me-1 h-4 w-4" />
            {t('financials.dispute')}
          </Button>
        )}
      </div>

      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('financials.approveStatement', { id: statementId })}</DialogTitle>
          </DialogHeader>
          <InvoiceUploadForm
            action="approve"
            statementId={statementId}
            onCancel={() => setShowApproveDialog(false)}
            onSubmit={async ({ statementId: sid, file, notes }) => {
              await onApprove(sid, file, notes)
              setShowApproveDialog(false)
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('financials.disputeStatement', { id: statementId })}</DialogTitle>
          </DialogHeader>
          <InvoiceUploadForm
            action="dispute"
            statementId={statementId}
            onCancel={() => setShowDisputeDialog(false)}
            onSubmit={async ({ statementId: sid, file, reason }) => {
              if (!reason) return
              await onDispute(sid, reason, file)
              setShowDisputeDialog(false)
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

